import { useDispatch, useSelector } from 'react-redux';
import React, { useRef } from 'react';
import { track } from '@amplitude/analytics-browser';
import {
	BROADCAST_STATES,
	STREAM_STATES,
} from '../../../stores/reducers/broadcasting';
import { GreenroomState as GS } from '../../../stores/types/types';
import { showAlert } from '../../general-components/alert-service';
import {
	RECORDING_OPTIONS,
	BROADCAST_TEXT,
	BROADCAST_CLASS,
} from '../constants';
import getRecordingLiveText from '../utils/getRecordingLiveText';
import Socket from '../../../connection/socket';
import * as BroadcastActions from '../../../stores/actions/broadcasting';
import { RESOLUTIONS } from '../../../stores/reducers/settings';
import { appLogger } from '../../../utils/datadog';
import { GetBroadcastingErrorLogs } from '../../../connection/broadcasting';
import { AmpEvent } from '../../../types/amplitude';
import {
	ScheduledStreamStatus,
	UpdateScheduledStreamStatus,
	scheduledStreamActiveStates,
} from '../../../connection/scheduled-streams';

interface DdLog {
    family: string;
	event: string;
    location: string;
    function: string;
    serverState: number;
    previousServerState: number;
    streamState: number;
    host: number;
    hovering: boolean;
    meeting: number | undefined;
    meeting_id: string | undefined;
}

const useBroadcastState = () => {
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const broadcastEndpoint = useSelector((s: GS) => s.BroadcastReducer.broadcastEndpoint);
	const useCustomEndpoint = useSelector((s: GS) => s.BroadcastReducer.useCustomEndpoint);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const selectedResolution = useSelector((s: GS) => s.SettingsReducer.selectedResolution);
	const currentShotId = useSelector((s: GS) => s.ShotsReducer.currentShotId);
	const serverState = useSelector((s: GS) => s.BroadcastReducer.serverState);
	const startingBroadcast = useSelector((s: GS) => s.BroadcastReducer.startingBroadcast);
	const stoppingBroadcast = useSelector((s: GS) => s.BroadcastReducer.stoppingBroadcast);
	const selectedRecordingOption = useSelector(
		(s: GS) => s.BroadcastReducer.selectedRecordingOption,
	);
	const scheduledStream = useSelector((s: GS) => s.MeetingReducer.scheduledStream);

	const dispatch = useDispatch();

	const startTimeout = useRef<NodeJS.Timeout | null>(null);

	const endActiveScheduledStream = () => {
		if (
			!token
			|| !meeting
			|| !scheduledStream
			|| !user
			|| !scheduledStreamActiveStates.includes(scheduledStream.status)
		) {
			return;
		}

		UpdateScheduledStreamStatus(
			token,
			meeting.meeting,
			scheduledStream.id,
			user.gr_user,
			ScheduledStreamStatus.Ended,
		);
	};

	// Hook to send a start message
	const startBroadcast = (
		setMockStartingBroadcast: React.Dispatch<React.SetStateAction<boolean>>,
	) => {
		if (!token || !meeting) return;
		// show starting broadcast in the UI
		setMockStartingBroadcast(true);
		// Check for broadcasting
		const isBroadcasting = selectedRecordingOption === RECORDING_OPTIONS.STREAM_AND_RECORD;
		// Stream resolution is what is selected by the stream destination card
		// Unless recording only, we select 1080p.
		let streamResolution = (RESOLUTIONS as any)[selectedResolution];
		if (!isBroadcasting) streamResolution = RESOLUTIONS['1080p'];

		track(isBroadcasting ? AmpEvent.streamStart : AmpEvent.recordStart);

		if (user?.gr_user) {
			dispatch(BroadcastActions.setBroadcastStartedBy(user.gr_user));
			dispatch(BroadcastActions.setBroadcastStartingShot(currentShotId));
			dispatch(BroadcastActions.setCurrentRecordingStreamId(''));
		}
		// Prevent users from spamming and causing a 500 error
		if (startTimeout.current) clearTimeout(startTimeout.current);
		startTimeout.current = setTimeout(() => {
			dispatch(
				BroadcastActions.startBroadcast(
					token,
					meeting.meeting,
					streamResolution,
					isBroadcasting,
					true,
				),
			);
		}, 5000);
	};

	// Hook to send a stop message
	const stopBroadcast = (
		startupTimer: NodeJS.Timeout,
		setIsStoppingRecording: React.Dispatch<React.SetStateAction<boolean>>,
		setMockStartingBroadcast: React.Dispatch<React.SetStateAction<boolean>>,
		setLockRecordingOption: React.Dispatch<React.SetStateAction<boolean>>,
		socket: Socket | null,
	) => {
		if (!token || !meeting) return;
		if (startupTimer) clearInterval(startupTimer);
		setIsStoppingRecording(true);
		setMockStartingBroadcast(false);
		setLockRecordingOption(false);

		dispatch(BroadcastActions.setBroadcastStartedBy(0));
		dispatch(BroadcastActions.setBroadcastStartingShot(0));
		dispatch(BroadcastActions.setCurrentRecordingStreamId(''));
		dispatch(BroadcastActions.setGoLiveButtonClicked(false));
		socket?.sendMessage('broadcast-off-show', true);

		if (startTimeout.current) clearTimeout(startTimeout.current);
		// Hack to prevent recordings from being cutoff. We don't want to wait 2s
		// if it isn't performing a recording.
		setTimeout(() => {
			setIsStoppingRecording(false);
			dispatch(BroadcastActions.stopBroadcast(token, meeting.meeting));
			endActiveScheduledStream();
		}, 2000);

		const timeStarted = new Date(meeting.broadcast_live_start_time).getTime();
		const seconds = Math.floor((Date.now() - timeStarted) / 1000);

		const event = selectedRecordingOption === RECORDING_OPTIONS.STREAM_AND_RECORD
			? AmpEvent.streamCancel
			: AmpEvent.recordCancel;

		track(event, { durationInSeconds: seconds });
	};

	// Hook to get the current broadcast state message
	const startStopBroadcast = (
		socket: Socket | null,
		stateMessage: string,
		clickedFromModal: boolean,
		streamStateRef: React.MutableRefObject<number>,
		setLockRecordingOption: React.Dispatch<React.SetStateAction<boolean>>,
		isStoppingRecording: boolean,
		setIsStoppingRecording: React.Dispatch<React.SetStateAction<boolean>>,
		setMockStartingBroadcast: React.Dispatch<React.SetStateAction<boolean>>,
		startupTimer: NodeJS.Timeout,
	) => {
		if (!token || !meeting) return;
		switch (serverState) {
			// start (or restart) broadcast
			case BROADCAST_STATES.BROADCAST_STOPPED:
			case BROADCAST_STATES.READY: {
				// Verify rtmp destination exists before going live.
				// New standalone GR meetings don't have default rtmp destinations until the producer
				// enters them in streaming setup. Do NOT throw this if only in record.
				//	Note, checking for not to provide backwards compatability when recording is toggled off
				if (
					!broadcastEndpoint?.url
                    && !useCustomEndpoint
                    && selectedRecordingOption !== RECORDING_OPTIONS.RECORD
				) {
					showAlert({
						message: `Cannot start ${getRecordingLiveText(
							selectedRecordingOption,
							isStoppingRecording,
							stateMessage,
						)}`,
						description: 'No destination is selected',
						duration: 5000,
					});
					// eslint-disable-next-line consistent-return
					return setMockStartingBroadcast(false);
				}

				if (
					stateMessage === BROADCAST_TEXT.GO_LIVE
                    || stateMessage === BROADCAST_TEXT.SHOW
                    || clickedFromModal
				) {
					startBroadcast(setMockStartingBroadcast);
					return;
				}

				if (stateMessage === BROADCAST_TEXT.CANCEL) {
					stopBroadcast(
						startupTimer,
						setIsStoppingRecording,
						setMockStartingBroadcast,
						setLockRecordingOption,
						socket,
					);
					return;
				}

				return;
			}

			// user clicked cancel when stream was starting up, cancel the broadcast
			case BROADCAST_STATES.BROADCAST_STARTING: {
				stopBroadcast(
					startupTimer,
					setIsStoppingRecording,
					setMockStartingBroadcast,
					setLockRecordingOption,
					socket,
				);
				return;
			}

			// queue up a start command for after server has started up
			case BROADCAST_STATES.STARTING_UP: {
				if (
					stateMessage === BROADCAST_TEXT.GO_LIVE
                    || clickedFromModal
				) {
					// Include the "Please Wait" alert here because if server just began starting up
					// when GO LIVE is clicked there could be a longer wait. Prewarming occurs after starting
					// up, and is usually quicker, so we don't include the alert message there
					// because you will probably be live momentarily.
					showAlert({
						message: 'Please Wait',
						description:
                            'Broadcasts outside of the event time window may take up to 10 minutes to begin',
						duration: 7000,
					});
					dispatch(
						BroadcastActions.autoStartBroadcast(
							token,
							meeting.meeting,
						),
					);
					return;
				}
				return;
			}

			// queue up a start command for after server has started up
			case BROADCAST_STATES.BROADCAST_PREWARMING: {
				if (
					stateMessage === BROADCAST_TEXT.GO_LIVE
                    || clickedFromModal
				) {
					dispatch(
						BroadcastActions.autoStartBroadcast(
							token,
							meeting.meeting,
						),
					);
					return;
				}
				return;
			}

			// restart broadcast that already was live
			case BROADCAST_STATES.OFF: {
				if (
					stateMessage === BROADCAST_TEXT.GO_LIVE
                    || clickedFromModal
				) {
					// Server hasn't started warming up yet. Will probably take a couple of minutes
					//  before broadcast is live.
					showAlert({
						message: 'Please Wait',
						description:
                            'Broadcasts outside of the event time window may take up to 10 minutes to begin',
						duration: 7000,
					});
					dispatch(
						BroadcastActions.manuallyCreateBroadcastServer(
							token,
							meeting.meeting,
						),
					);
					return;
				}
				return;
			}

			// broadcast is in progress and user clicked button. This means they want to either
			// go from broadcast off -> main stream
			// go from main stream -> broadcast off
			case BROADCAST_STATES.BROADCAST_STARTED: {
				// they were in the post stream, go back to canvas view OR we were showing
				// pre-media, start main show
				if (
					streamStateRef.current === STREAM_STATES.POST
                    || streamStateRef.current === STREAM_STATES.PRE
				) {
					socket?.sendMessage('broadcast-main-show', true);
					return;
				}
				// no post-stream media, ok to just end
				stopBroadcast(
					startupTimer,
					setIsStoppingRecording,
					setMockStartingBroadcast,
					setLockRecordingOption,
					socket,
				);
				return;
			}

			// attempt to restart broadcast that previously errored out
			// Do NOT throw this if only in record.
			//	Note, checking for not to provide backwards compatability when recording is toggled off
			case BROADCAST_STATES.BROADCAST_FAILED: {
				if (
					!broadcastEndpoint?.url
                    && !useCustomEndpoint
                    && selectedRecordingOption !== RECORDING_OPTIONS.RECORD
				) {
					showAlert({
						message: 'Cannot start broadcast',
						description: 'No destination is selected',
						duration: 5000,
					});
					// eslint-disable-next-line consistent-return
					return setMockStartingBroadcast(false);
				}

				dispatch(
					BroadcastActions.setBroadcastState(
						BROADCAST_STATES.BROADCAST_STOPPED,
					),
				);

				stopBroadcast(
					startupTimer,
					setIsStoppingRecording,
					setMockStartingBroadcast,
					setLockRecordingOption,
					socket,
				);

				startBroadcast(setMockStartingBroadcast);
				break;
			}

			default:
				break;
		}
	};

	const getBroadcastState = (
		hovering: boolean,
		goLiveEnabled: boolean,
		streamStateRef: React.MutableRefObject<number>,
		mockStartingBroadcast: boolean,
		ddLog: DdLog,
	): [string, string, boolean, boolean] => {
		switch (serverState) {
			case BROADCAST_STATES.OFF:
			case BROADCAST_STATES.STARTING_UP:
			case BROADCAST_STATES.BROADCAST_PREWARMING:
				if (host) {
					return [
						BROADCAST_TEXT.GETTING_READY,
						BROADCAST_CLASS.LOADING,
						true,
						false,
					];
				}
				// presenters should never see the button unless it's in a play state, so it doesn't
				// really matter what we show them here.
				return [
					BROADCAST_TEXT.SHOW,
					BROADCAST_CLASS.READY,
					false,
					false,
				];
			case BROADCAST_STATES.BROADCAST_STARTING: {
				if (host) {
					if (hovering) {
						return [
							BROADCAST_TEXT.CANCEL,
							BROADCAST_CLASS.CANCEL,
							false,
							false,
						];
					}

					return [
						BROADCAST_TEXT.STARTING_BROADCAST,
						BROADCAST_CLASS.LOADING,
						true,
						false,
					];
				}

				return [
					BROADCAST_TEXT.SHOW,
					BROADCAST_CLASS.READY,
					false,
					false,
				];
			}

			case BROADCAST_STATES.READY: {
				if (startingBroadcast) {
					if (hovering) {
						return [
							BROADCAST_TEXT.CANCEL,
							BROADCAST_CLASS.CANCEL,
							false,
							false,
						];
					}

					return [
						BROADCAST_TEXT.STARTING_BROADCAST,
						BROADCAST_CLASS.LOADING,
						true,
						false,
					];
				}
				if (host) {
					if (hovering && mockStartingBroadcast) {
						return [
							BROADCAST_TEXT.CANCEL,
							BROADCAST_CLASS.CANCEL,
							false,
							false,
						];
					}
					return [
						BROADCAST_TEXT.GO_LIVE,
						BROADCAST_CLASS.READY,
						false,
						false,
					];
				}
				return [
					BROADCAST_TEXT.SHOW,
					BROADCAST_CLASS.READY,
					false,
					false,
				];
			}

			case BROADCAST_STATES.BROADCAST_STARTED: {
				if (stoppingBroadcast) {
					return [
						BROADCAST_TEXT.GO_LIVE,
						BROADCAST_CLASS.READY,
						false,
						false,
					];
				}
				if (host && hovering) {
					if (
						streamStateRef.current === STREAM_STATES.PRE
                        && goLiveEnabled
					) {
						return [
							BROADCAST_TEXT.GO_LIVE,
							BROADCAST_CLASS.READY,
							false,
							false,
						];
					}

					if (
						streamStateRef.current === STREAM_STATES.POST
                        && goLiveEnabled
					) {
						return [
							BROADCAST_TEXT.GO_LIVE,
							BROADCAST_CLASS.READY,
							false,
							false,
						];
					}

					return [
						BROADCAST_TEXT.END_SHOW,
						BROADCAST_CLASS.LIVE,
						false,
						false,
					];
				}

				if (host) {
					if (streamStateRef.current === STREAM_STATES.PRE) {
						return [
							BROADCAST_TEXT.GO_LIVE,
							BROADCAST_CLASS.READY,
							false,
							false,
						];
					}
					if (streamStateRef.current === STREAM_STATES.POST) {
						return [
							BROADCAST_TEXT.GO_LIVE,
							BROADCAST_CLASS.STOPPED,
							false,
							false,
						];
					}
				}

				return [
					BROADCAST_TEXT.LIVE,
					BROADCAST_CLASS.LIVE,
					false,
					false,
				];
			}

			case BROADCAST_STATES.BROADCAST_STOPPED: {
				if (host && mockStartingBroadcast) {
					if (hovering) {
						return [
							BROADCAST_TEXT.CANCEL,
							BROADCAST_CLASS.CANCEL,
							false,
							false,
						];
					}
					return [
						BROADCAST_TEXT.STARTING_BROADCAST,
						BROADCAST_CLASS.LOADING,
						true,
						false,
					];
				}
				if (host && hovering && goLiveEnabled) {
					return [
						BROADCAST_TEXT.GO_LIVE,
						BROADCAST_CLASS.READY,
						false,
						false,
					];
				}
				if (host && goLiveEnabled) {
					return [
						BROADCAST_TEXT.GO_LIVE,
						BROADCAST_CLASS.READY,
						false,
						false,
					];
				}
				return [
					BROADCAST_TEXT.SHOW,
					BROADCAST_CLASS.READY,
					false,
					false,
				];
			}

			case BROADCAST_STATES.BROADCAST_PREWARM_FAILED:
			case BROADCAST_STATES.BROADCAST_FAILED: {
				const updatedDdLog = {
					...ddLog,
					function: `${ddLog.function}:failureSwitch`,
				};
				appLogger.error('Broadcast failed', updatedDdLog);

				if (meeting) GetBroadcastingErrorLogs(meeting.meeting);

				endActiveScheduledStream();

				return [
					BROADCAST_TEXT.SHOW,
					BROADCAST_CLASS.READY,
					false,
					true,
				];
			}

			case BROADCAST_STATES.DRAINING:
			case BROADCAST_STATES.KILLED: {
				return [
					BROADCAST_TEXT.GO_LIVE,
					BROADCAST_CLASS.STOPPED,
					false,
					true,
				];
			}

			default:
				return [
					BROADCAST_TEXT.SHOW,
					BROADCAST_CLASS.READY,
					true,
					false,
				];
		}
	};

	return {
		getBroadcastState,
		startStopBroadcast,
	};
};

export default useBroadcastState;
